import { Badge } from "@shared/ui/badge";
import { Checkbox } from "@shared/ui/checkbox";
import { Calendar, FileText } from "lucide-react";
import React from "react";

interface BillCardProps {
  bill: any;
  isSelected: boolean;
  onToggle: (billId: string) => void;
  currency: string;
  isMultiSelectMode?: boolean;
}

export const BillCard: React.FC<BillCardProps> = ({
  bill,
  isSelected,
  onToggle,
  currency,
  isMultiSelectMode,
}) => {
  const getBillStatusBadge = (status: any["billStatus"]) => {
    const s = String(status || "").toUpperCase();
    return s === "OVERDUE"
      ? "bg-red-50 text-red-600 border-red-200"
      : s === "GENERATED" 
      ? "bg-blue-50 text-blue-600 border-blue-200"
      : "bg-blue-50 text-blue-600 border-blue-200";
  };

  const getPaymentStatusBadge = (status: any["paymentStatus"]) => {
    return status === "Partially Paid"
      ? "bg-yellow-50 text-yellow-700 border-yellow-200"
      : status === "Unpaid"
      ? "bg-red-50 text-red-600 border-red-200"
      : "bg-orange-50 text-orange-600 border-orange-200";
  };

  return (
    <div
      className={`border rounded-lg p-3 cursor-pointer transition-all ${
        isSelected
          ? "border-blue-500 bg-[#eff5ff]"
          : "border-gray-200 hover:border-gray-300 bg-white"
      }`}
      onClick={() => onToggle(bill.invoiceNo)}
    >
      <div className="flex items-center gap-3">
        {isMultiSelectMode && (
          <div className="flex items-center">
            <Checkbox checked={isSelected} className="w-[18px] h-[18px] border-blue-500 data-[state=checked]:bg-blue-500" />
          </div>
        )}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 rounded-md bg-white border border-gray-200 flex items-center justify-center">
            <FileText className="w-4 h-4 text-blue-500" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Top row */}
          <div className="flex justify-between items-center">
            <div>
              <h4 className="font-bold text-gray-900 text-[13px] mb-0.5">
                {bill.invoiceNo}
              </h4>
              <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                <span>{bill.billDate ? new Date(bill.billDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : ""}</span>

                <Badge
                  className={`px-1.5 py-0 font-medium border ${getBillStatusBadge(
                    bill.billStatus,
                  )}`}
                >
                  {bill.billStatus}
                </Badge>

                <Badge
                  className={`px-1.5 py-0 font-medium border ${getPaymentStatusBadge(
                    bill.billPaymentStatus,
                  )}`}
                >
                  {bill.billPaymentStatus}
                </Badge>
              </div>
            </div>

            <div className="text-right">
              <p className="text-[14px] font-bold text-gray-900 leading-tight">
                {currency} {bill.outstandingAmount}
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Due: <span>{bill.dueDate}</span>
              </p>
            </div>
          </div>

          {/* Partially Paid Details */}
          {bill.billPaymentStatus === "Partially Paid" &&
            bill.outstandingAmount && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2 font-medium">
                  Last Payment Details
                </p>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Total Bill</span>
                    <p className="font-medium">
                      {currency} {bill.billAmount}
                    </p>
                  </div>

                  <div>
                    <span className="text-gray-600">Last Payment</span>
                    <p className="font-medium">
                      {currency} {bill.lastPaymentAmount}
                    </p>
                    <p className="text-xs text-gray-500">
                      {bill.lastPaymentDate}
                    </p>
                  </div>
                </div>

                <div className="mt-3">
                  <span className="text-gray-600">Outstanding</span>
                  <p className="font-semibold text-red-600">
                    {currency} {bill.outstandingAmount}
                  </p>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
